import React, { useState, useEffect, useReducer } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Chip,
  Grid,
  Paper,
  Divider,
  FormHelperText,
  Autocomplete,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Button,
  ButtonGroup,
  Alert
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  DateRange as DateIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';

import '../../index.css';
// ========== CRON FIELD REDUCER ==========
const cronFieldReducer = (state, action) => {
  const { field, property, value } = action;

  if (action.type === 'SET_FIELD') {
    return {
      ...state,
      [field]: {
        ...state[field],
        [property]: value
      }
    };
  }

  if (action.type === 'SET_ENTIRE_FIELD') {
    return {
      ...state,
      [field]: value
    };
  }

  if (action.type === 'RESET_ALL') {
    return action.value;
  }

  return state;
};

const getInitialCronFieldsState = () => ({
  minute: { mode: 'every', specific: 0, interval: 1, rangeFrom: 0, rangeTo: 59, multiple: [] },
  hour: { mode: 'every', specific: 0, interval: 1, rangeFrom: 0, rangeTo: 23, multiple: [] },
  dayOfMonth: { mode: 'every', specific: 1, interval: 1, rangeFrom: 1, rangeTo: 31, multiple: [] },
  month: { mode: 'every', specific: 1, interval: 1, rangeFrom: 1, rangeTo: 12, multiple: [] },
  dayOfWeek: { mode: 'every', specific: 1, interval: 1, rangeFrom: 0, rangeTo: 6, multiple: [] }
});

const ScheduleTriggerConfig = ({ value = {}, onChange }) => {
  // Simple schedule configuration state
  const [scheduleType, setScheduleType] = useState(value.scheduleType || 'minutes');
  const [interval, setInterval] = useState(value.interval || 5);
  const [unit, setUnit] = useState(value.unit || 'minutes');
  const [hour, setHour] = useState(value.hour || 9);
  const [minute, setMinute] = useState(value.minute || 0);
  const [dayOfWeek, setDayOfWeek] = useState(value.dayOfWeek || 1);
  const [dayOfMonth, setDayOfMonth] = useState(value.dayOfMonth || 1);
  const [cronExpression, setCronExpression] = useState(value.cronExpression || '0 9 * * *');
  const [previewText, setPreviewText] = useState('');

  // Visual Cron Builder State
  const [advancedMode, setAdvancedMode] = useState(false);

  // Cron fields state - REFACTORED: Single useReducer instead of 36 useState
  const [cronFields, dispatchCronField] = useReducer(cronFieldReducer, null, getInitialCronFieldsState);

  const intervalTypes = [
    {
      value: 'minutes',
      label: 'Every X minutes',
      icon: <TimeIcon />,
      showNumberInput: true,
      max: 60,
      min: 1,
      defaultValue: 5
    },
    {
      value: 'hours',
      label: 'Every X hours',
      icon: <TimeIcon />,
      showNumberInput: true,
      max: 24,
      min: 1,
      defaultValue: 1
    },
    {
      value: 'days',
      label: 'Every X days',
      icon: <DateIcon />,
      showNumberInput: true,
      max: 30,
      min: 1,
      defaultValue: 1
    },
    {
      value: 'daily',
      label: 'Daily',
      icon: <ScheduleIcon />,
      showTimePicker: true
    },
    {
      value: 'weekly',
      label: 'Weekly',
      icon: <DateIcon />,
      showDaySelector: true,
      showTimePicker: true
    },
    {
      value: 'monthly',
      label: 'Monthly',
      icon: <DateIcon />,
      showDateSelector: true,
      showTimePicker: true
    },
    {
      value: 'custom',
      label: 'Custom (Cron)',
      icon: <CodeIcon />,
      showCronInput: true
    }
  ];

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  // Common cron presets for quick selection
  const cronPresets = [
    { label: 'Every minute', cron: '* * * * *', description: 'Runs every single minute' },
    { label: 'Every 5 min', cron: '*/5 * * * *', description: 'Runs every 5 minutes' },
    { label: 'Every 15 min', cron: '*/15 * * * *', description: 'Runs every 15 minutes' },
    { label: 'Every hour', cron: '0 * * * *', description: 'Runs at the start of every hour' },
    { label: 'Every day 9AM', cron: '0 9 * * *', description: 'Runs daily at 9:00 AM' },
    { label: 'Weekdays 9AM', cron: '0 9 * * 1-5', description: 'Runs Mon-Fri at 9:00 AM' },
    { label: 'Business hours', cron: '0 9-17 * * 1-5', description: 'Every hour from 9AM-5PM on weekdays' },
    { label: 'First of month', cron: '0 0 1 * *', description: 'Runs at midnight on the 1st of each month' }
  ];

  /**
   * Apply a preset cron expression
   */
  const applyPreset = (cronExpr) => {
    setCronExpression(cronExpr);
    // Parse the preset into visual builder
    parseCronToBuilder(cronExpr);
  };

  const generatePreview = () => {
    switch (scheduleType) {
      case 'minutes':
        return `Every ${interval} minute${interval !== 1 ? 's' : ''}`;
      case 'hours':
        return `Every ${interval} hour${interval !== 1 ? 's' : ''}`;
      case 'days':
        return `Every ${interval} day${interval !== 1 ? 's' : ''}`;
      case 'daily':
        return `Daily at ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      case 'weekly':
        const dayName = daysOfWeek.find(d => d.value === dayOfWeek)?.label || 'Monday';
        return `Every ${dayName} at ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      case 'monthly':
        return `Monthly on day ${dayOfMonth} at ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      case 'custom':
        return parseCronExpression(cronExpression);
      default:
        return '';
    }
  };

  // Utility function from SchedulePreview - calculates next run time
  const generateNextRun = () => {
    if (!scheduleType) return null;

    const now = new Date();
    let nextRun;

    try {
      switch (scheduleType) {
        case 'minutes':
          nextRun = new Date(now.getTime() + (interval || 5) * 60 * 1000);
          break;
        case 'hours':
          nextRun = new Date(now.getTime() + (interval || 1) * 60 * 60 * 1000);
          break;
        case 'days':
          nextRun = new Date(now.getTime() + (interval || 1) * 24 * 60 * 60 * 1000);
          break;
        case 'daily':
          nextRun = new Date();
          nextRun.setHours(hour || 9, minute || 0, 0, 0);
          if (nextRun <= now) {
            nextRun.setDate(nextRun.getDate() + 1);
          }
          break;
        case 'weekly':
          nextRun = new Date();
          const targetDay = dayOfWeek || 1;
          const currentDay = nextRun.getDay();
          const daysUntil = (targetDay - currentDay + 7) % 7 || 7;
          nextRun.setDate(nextRun.getDate() + daysUntil);
          nextRun.setHours(hour || 9, minute || 0, 0, 0);
          if (nextRun <= now && daysUntil === 7) {
            nextRun.setDate(nextRun.getDate() + 7);
          }
          break;
        case 'monthly':
          nextRun = new Date();
          nextRun.setDate(dayOfMonth || 1);
          nextRun.setHours(hour || 9, minute || 0, 0, 0);
          if (nextRun <= now) {
            nextRun.setMonth(nextRun.getMonth() + 1);
          }
          break;
        default:
          return null;
      }

      return nextRun;
    } catch {
      return null;
    }
  };

  // Utility function from SchedulePreview - formats next run time
  const formatNextRun = (date) => {
    if (!date) return 'Unable to calculate';

    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    if (diffMs < 60000) { // Less than 1 minute
      return 'In a few seconds';
    } else if (diffMs < 3600000) { // Less than 1 hour
      const minutes = Math.floor(diffMs / 60000);
      return `In ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (diffMs < 86400000) { // Less than 1 day
      const hours = Math.floor(diffMs / 3600000);
      return `In ${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  const parseCronExpression = (cron) => {
    try {
      const parts = cron.split(' ');
      if (parts.length !== 5 && parts.length !== 6) return 'Invalid cron expression';

      // Support both 5-field and 6-field (with seconds)
      const [min, hr, day, month, weekday] = parts.length === 5 ? parts : parts.slice(1);

      if (min === '0' && hr !== '*' && day === '*' && month === '*' && weekday === '*') {
        return `Daily at ${hr}:00`;
      }
      if (min === '0' && hr !== '*' && day === '*' && month === '*' && weekday !== '*') {
        const dayName = daysOfWeek.find(d => d.value === parseInt(weekday))?.label || 'Unknown';
        return `Every ${dayName} at ${hr}:00`;
      }
      if (min.startsWith('*/') && hr === '*') {
        return `Every ${min.slice(2)} minutes`;
      }

      return `Custom: ${cron}`;
    } catch {
      return 'Invalid cron expression';
    }
  };

  // ========== CRON HELPER FUNCTIONS ==========


  const updateCronField = (field, property, value) => {
    dispatchCronField({ type: 'SET_FIELD', field, property, value });
  };

  const createNumberChangeHandler = (field, property, min, max) => (e) => {
    const val = e.target.value;
    if (val === '') {
      updateCronField(field, property, '');
    } else {
      const num = parseInt(val);
      if (!isNaN(num)) {
        updateCronField(field, property, Math.max(min, Math.min(max, num)));
      }
    }
  };

  const createNumberBlurHandler = (field, property, defaultValue) => (e) => {
    if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
      updateCronField(field, property, defaultValue);
    }
  };

  const generateCronFromBuilder = () => {
    const minute = generateCronPart('minute', cronFields.minute.mode, cronFields.minute);
    const hour = generateCronPart('hour', cronFields.hour.mode, cronFields.hour);
    const dayOfMonthPart = generateCronPart('dayOfMonth', cronFields.dayOfMonth.mode, cronFields.dayOfMonth);
    const monthPart = generateCronPart('month', cronFields.month.mode, cronFields.month);
    const dayOfWeekPart = generateCronPart('dayOfWeek', cronFields.dayOfWeek.mode, cronFields.dayOfWeek);

    // 5-field cron format (minute hour day month weekday)
    return `${minute} ${hour} ${dayOfMonthPart} ${monthPart} ${dayOfWeekPart}`;
  };

  /**
   * Generate individual cron part based on mode
   */
  const generateCronPart = (field, mode, values) => {
    switch (mode) {
      case 'every':
        return '*';

      case 'specific':
        // Handle empty string during typing
        return values.specific === '' ? '0' : String(values.specific);

      case 'interval':
        // Handle empty string during typing
        return `*/${values.interval === '' ? '1' : values.interval}`;

      case 'range':
        // Handle empty strings during typing
        const from = values.rangeFrom === '' ? '0' : values.rangeFrom;
        const to = values.rangeTo === '' ? '59' : values.rangeTo;
        return `${from}-${to}`;

      case 'multiple':
        return values.multiple.length > 0 ? values.multiple.join(',') : '*';

      default:
        return '*';
    }
  };

  const parseCronToBuilder = (cronString) => {
    try {
      const parts = cronString.trim().split(/\s+/);

      let minute, hour, day, month, weekday;

      if (parts.length === 5) {
        // 5-field format: minute hour day month weekday
        [minute, hour, day, month, weekday] = parts;
      } else {
        return false;
      }

      // Parse all fields using helper function
      dispatchCronField({ type: 'SET_ENTIRE_FIELD', field: 'minute', value: parseCronFieldToState(minute) });
      dispatchCronField({ type: 'SET_ENTIRE_FIELD', field: 'hour', value: parseCronFieldToState(hour) });
      dispatchCronField({ type: 'SET_ENTIRE_FIELD', field: 'dayOfMonth', value: parseCronFieldToState(day) });
      dispatchCronField({ type: 'SET_ENTIRE_FIELD', field: 'month', value: parseCronFieldToState(month) });
      dispatchCronField({ type: 'SET_ENTIRE_FIELD', field: 'dayOfWeek', value: parseCronFieldToState(weekday) });

      return true;
    } catch (error) {
      console.error('Failed to parse cron expression:', error);
      return false;
    }
  };


  const parseCronFieldToState = (value) => {
    const fieldState = {
      mode: 'every',
      specific: 0,
      interval: 1,
      rangeFrom: 0,
      rangeTo: 59,
      multiple: []
    };

    if (value === '*') {
      fieldState.mode = 'every';
    } else if (value.includes('*/')) {
      fieldState.mode = 'interval';
      fieldState.interval = parseInt(value.split('/')[1]) || 1;
    } else if (value.includes('-')) {
      fieldState.mode = 'range';
      const [from, to] = value.split('-').map(v => parseInt(v));
      fieldState.rangeFrom = from || 0;
      fieldState.rangeTo = to || 59;
    } else if (value.includes(',')) {
      fieldState.mode = 'multiple';
      fieldState.multiple = value.split(',').map(v => parseInt(v));
    } else {
      fieldState.mode = 'specific';
      fieldState.specific = parseInt(value) || 0;
    }

    return fieldState;
  };

  /**
   * Generate human-readable description of cron
   */
  const getCronDescription = (cronString) => {
    try {
      const parts = cronString.trim().split(/\s+/);

      let min, hr, day, month, weekday, sec;

      if (parts.length === 5) {
        // 5-field format: minute hour day month weekday
        [min, hr, day, month, weekday] = parts;
      } else if (parts.length === 6) {
        // 6-field format: second minute hour day month weekday
        [sec, min, hr, day, month, weekday] = parts;
      } else {
        return 'Invalid cron expression';
      }

      let description = [];

      // Second description (if 6-field format)
      if (sec && sec !== '*' && sec !== '0') {
        if (sec.includes('*/')) {
          description.push(`Every ${sec.split('/')[1]} second(s)`);
        } else if (sec.includes('-')) {
          description.push(`At seconds ${sec}`);
        } else if (sec.includes(',')) {
          description.push(`At seconds ${sec}`);
        } else {
          description.push(`At second ${sec}`);
        }
      }

      // Minute description
      if (min === '*') {
        description.push('Every minute');
      } else if (min.includes('*/')) {
        description.push(`Every ${min.split('/')[1]} minute(s)`);
      } else if (min.includes('-')) {
        description.push(`At minutes ${min}`);
      } else if (min.includes(',')) {
        description.push(`At minutes ${min}`);
      } else {
        description.push(`At minute ${min}`);
      }

      // Hour description
      if (hr !== '*') {
        if (hr.includes('-')) {
          const [from, to] = hr.split('-');
          description.push(`between ${formatHourDisplay(from)} - ${formatHourDisplay(to)}`);
        } else if (hr.includes('*/')) {
          description.push(`every ${hr.split('/')[1]} hour(s)`);
        } else if (hr.includes(',')) {
          description.push(`at hours ${hr}`);
        } else {
          description.push(`at ${formatHourDisplay(hr)}`);
        }
      }

      // Day of month description
      if (day !== '*') {
        if (day.includes(',')) {
          description.push(`on days ${day}`);
        } else if (day.includes('-')) {
          description.push(`on days ${day}`);
        } else {
          description.push(`on day ${day}`);
        }
      }

      // Month description
      if (month !== '*') {
        const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month.includes(',')) {
          const months = month.split(',').map(m => monthNames[parseInt(m)]).join(', ');
          description.push(`in ${months}`);
        } else {
          description.push(`in ${monthNames[parseInt(month)]}`);
        }
      }

      // Day of week description
      if (weekday !== '*' && weekday !== '*/1') {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        if (weekday.includes(',')) {
          const days = weekday.split(',').map(d => dayNames[parseInt(d)]).join(', ');
          description.push(`on ${days}`);
        } else if (weekday.includes('-')) {
          description.push(`on weekdays ${weekday}`);
        } else if (weekday.includes('*/')) {
          // Handle */N patterns (e.g., */2 = every other day)
          const interval = parseInt(weekday.split('/')[1]);
          if (interval === 1) {
            description.push('every day');
          } else {
            description.push(`every ${interval} days of the week`);
          }
        } else {
          const dayIndex = parseInt(weekday);
          if (!isNaN(dayIndex) && dayIndex >= 0 && dayIndex <= 6) {
            description.push(`on ${dayNames[dayIndex]}`);
          }
        }
      }

      return description.join(' ');
    } catch (error) {
      return 'Unable to parse cron expression';
    }
  };

  const formatHourDisplay = (hour) => {
    const h = parseInt(hour);
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
  };

  // ========== VISUAL CRON BUILDER RENDER FUNCTIONS ==========

  const renderCronFieldInputs = (fieldState, fieldName, min, max, options) => {
    const { mode } = fieldState;

    return (
      <>
        {mode === 'specific' && (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={`${fieldName} (${min}-${max})`}
            value={fieldState.specific}
            onChange={createNumberChangeHandler(fieldName, 'specific', min, max)}
            onBlur={createNumberBlurHandler(fieldName, 'specific', min)}
            inputProps={{ min, max }}
            helperText={`Choose a specific ${fieldName.toLowerCase()} (${min}-${max})`}
          />
        )}

        {mode === 'interval' && (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={`Every N ${fieldName.toLowerCase()}s`}
            value={fieldState.interval}
            onChange={createNumberChangeHandler(fieldName, 'interval', 1, max)}
            onBlur={createNumberBlurHandler(fieldName, 'interval', 1)}
            inputProps={{ min: 1, max }}
            helperText={`Run every N ${fieldName.toLowerCase()}s (1-${max})`}
          />
        )}

        {mode === 'range' && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={`From (${min}-${max})`}
              value={fieldState.rangeFrom}
              onChange={createNumberChangeHandler(fieldName, 'rangeFrom', min, max)}
              onBlur={createNumberBlurHandler(fieldName, 'rangeFrom', min)}
              inputProps={{ min, max }}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label={`To (${min}-${max})`}
              value={fieldState.rangeTo}
              onChange={createNumberChangeHandler(fieldName, 'rangeTo', min, max)}
              onBlur={createNumberBlurHandler(fieldName, 'rangeTo', max)}
              inputProps={{ min, max }}
            />
          </Box>
        )}

        {mode === 'multiple' && !options && (
          <Autocomplete
            multiple
            size="small"
            options={Array.from({ length: max - min + 1 }, (_, i) => i + min)}
            value={fieldState.multiple}
            onChange={(e, newValue) => updateCronField(fieldName, 'multiple', newValue)}
            renderInput={(params) => <TextField {...params} label={`Select ${fieldName.toLowerCase()}s (${min}-${max})`} />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
              ))
            }
          />
        )}

        {mode === 'multiple' && options && (
          <Autocomplete
            multiple
            size="small"
            options={options}
            value={fieldState.multiple.map(m => options.find(o => o.value === m))}
            onChange={(e, newValue) => updateCronField(fieldName, 'multiple', newValue.map(v => v.value))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => <TextField {...params} label={`Select ${fieldName.toLowerCase()}s`} />}
          />
        )}
      </>
    );
  };


  const renderCronFieldSelector = (label, icon, fieldName, modeOptions, min, max, options = null) => {
    const fieldState = cronFields[fieldName];
    const mode = fieldState.mode;

    return (
      <Accordion
        defaultExpanded={mode !== 'every'}
        sx={{
          mb: 2,
          '&:before': { display: 'none' }, // Remove default divider
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px !important',
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Typography variant="subtitle1" fontWeight={600}>
              {label}
            </Typography>
            <Chip
              label={mode === 'every' ? '*' : mode}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }}
            />
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 3 }}>
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <InputLabel>Mode</InputLabel>
            <Select
              value={mode}
              label="Mode"
              onChange={(e) => updateCronField(fieldName, 'mode', e.target.value)}
            >
              {modeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ minHeight: mode === 'every' ? '0' : '60px' }}>
            {renderCronFieldInputs(fieldState, fieldName, min, max, options)}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  /**
   * Render visual cron builder (main builder UI)
   */
  const renderVisualCronBuilder = () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return (
      <Box sx={{ mt: 2 }}>
        {/* QUICK PRESETS */}
        <Alert icon={<LightbulbIcon fontSize="inherit" />} severity="info" sx={{ mb: 3 }}>
          <h5>Quick Presets - Click to apply common schedules:</h5>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            {cronPresets.map((preset) => (
              <Tooltip key={preset.cron} title={preset.description} arrow>
                <Button
                  size="small"
                  variant="outlined"
                  className="btn-time"
                  onClick={() => applyPreset(preset.cron)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {preset.label}
                </Button>
              </Tooltip>
            ))}
          </Box>
        </Alert>


        {/* MINUTE FIELD */}
        {renderCronFieldSelector(
          'Minutes',
          <TimeIcon color="primary" />,
          'minute',
          [
            { value: 'every', label: 'Every minute (*)' },
            { value: 'specific', label: 'Specific minute' },
            { value: 'interval', label: 'Every N minutes' },
            { value: 'range', label: 'Minute range' },
            { value: 'multiple', label: 'Multiple minutes' }
          ],
          0,
          59
        )}

        {/* HOUR FIELD */}
        {renderCronFieldSelector(
          'Hours',
          <TimeIcon color="primary" />,
          'hour',
          [
            { value: 'every', label: 'Every hour (*)' },
            { value: 'specific', label: 'Specific hour' },
            { value: 'interval', label: 'Every N hours' },
            { value: 'range', label: 'Hour range' },
            { value: 'multiple', label: 'Multiple hours' }
          ],
          0,
          23
        )}

        {/* DAY OF MONTH FIELD */}
        {renderCronFieldSelector(
          'Day of Month',
          <DateIcon color="primary" />,
          'dayOfMonth',
          [
            { value: 'every', label: 'Every day (*)' },
            { value: 'specific', label: 'Specific day' },
            { value: 'interval', label: 'Every N days' },
            { value: 'range', label: 'Day range' },
            { value: 'multiple', label: 'Multiple days' }
          ],
          1,
          31
        )}

        {/* MONTH FIELD */}
        {renderCronFieldSelector(
          'Month',
          <DateIcon color="primary" />,
          'month',
          [
            { value: 'every', label: 'Every month (*)' },
            { value: 'specific', label: 'Specific month' },
            { value: 'interval', label: 'Every N months' },
            { value: 'range', label: 'Month range' },
            { value: 'multiple', label: 'Multiple months' }
          ],
          1,
          12,
          monthNames.map((name, idx) => ({ value: idx + 1, label: name }))
        )}

        {/* DAY OF WEEK FIELD */}
        {renderCronFieldSelector(
          'Day of Week',
          <DateIcon color="primary" />,
          'dayOfWeek',
          [
            { value: 'every', label: 'Every day (*)' },
            { value: 'specific', label: 'Specific day' },
            { value: 'interval', label: 'Every N days' },
            { value: 'range', label: 'Day range' },
            { value: 'multiple', label: 'Multiple days' }
          ],
          0,
          6,
          dayNames.map((name, idx) => ({ value: idx, label: name }))
        )}

        {/* GENERATED CRON PREVIEW */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mt: 3,
            backgroundColor: '#F6F7FF',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CodeIcon />
            <Typography variant="h6" fontWeight={700} color="primary.main">
              Generated Cron Expression
            </Typography>
          </Box>

          <Chip
            label={generateCronFromBuilder()}
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              height: '36px',
              backgroundColor: '#EFF6FF',
              color: '#2563EB',
              mb: 2
            }}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            📝 Human-Readable Description:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {getCronDescription(generateCronFromBuilder())}
          </Typography>
        </Paper>
      </Box>
    );
  };

  const updateConfig = () => {
    // Return the complete schedule configuration
    // This will be saved to config.schedule field
    const scheduleConfig = {
      type: 'schedule',
      triggerType: 'schedule',
      scheduleType,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    // Add schedule-specific data
    switch (scheduleType) {
      case 'minutes':
      case 'hours':
      case 'days':
        scheduleConfig.interval = interval;
        scheduleConfig.unit = scheduleType;
        break;
      case 'daily':
        scheduleConfig.hour = hour;
        scheduleConfig.minute = minute;
        break;
      case 'weekly':
        scheduleConfig.dayOfWeek = dayOfWeek;
        scheduleConfig.hour = hour;
        scheduleConfig.minute = minute;
        break;
      case 'monthly':
        scheduleConfig.dayOfMonth = dayOfMonth;
        scheduleConfig.hour = hour;
        scheduleConfig.minute = minute;
        break;
      case 'custom':
        scheduleConfig.cronExpression = cronExpression;
        break;
    }

    onChange?.(scheduleConfig);
  };

  useEffect(() => {
    setPreviewText(generatePreview());

    // Update cron expression from visual builder when in custom mode and not advanced mode
    if (scheduleType === 'custom' && !advancedMode) {
      const generatedCron = generateCronFromBuilder();
      if (generatedCron !== cronExpression) {
        setCronExpression(generatedCron);
      }
    }

    // Only update config if we have a value prop (meaning this field is actually being used)
    if (value !== undefined) {
      updateConfig();
    }
  }, [
    scheduleType, interval, unit, hour, minute, dayOfWeek, dayOfMonth, cronExpression, value,
    // REFACTORED: Use cronFields instead of individual states
    advancedMode, cronFields
  ]);

  /**
   * Parse existing cron expression when component mounts or cronExpression changes
   */
  useEffect(() => {
    if (scheduleType === 'custom' && cronExpression && !advancedMode) {
      const success = parseCronToBuilder(cronExpression);
      if (!success) {
        // If parsing fails, switch to advanced mode
        setAdvancedMode(true);
      }
    }
  }, []); // Run only on mount

  const currentType = intervalTypes.find(type => type.value === scheduleType);

  return (
    <Box sx={{ mt: 2 }}>
      <h5>
        <i className='icon'><ScheduleIcon color="primary" style={{ color: 'var(--primary-color)', width: '15px', height: '15px', marginRight: '8px' }} /></i>
        Schedule Configuration
      </h5>
      <Box className="schedule-box-workflow" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div className='group-form'>
          <label>Trigger Interval</label>
          {/* Trigger Interval Selector */}
          <FormControl fullWidth size="medium">
            <Select
              value={scheduleType}
              placeholder="Trigger Interval"
              onChange={(e) => setScheduleType(e.target.value)}
            >
              {intervalTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {type.icon}
                    {type.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Dynamic Configuration Inputs */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Number Input for Intervals */}
          {currentType?.showNumberInput && (
            <div className='group-form runtime'>
              <label>Run every</label>
              <TextField
                fullWidth
                placeholder="Run every"
                type="number"
                value={interval}
                onChange={(e) => setInterval(Math.max(currentType.min, Math.min(currentType.max, parseInt(e.target.value) || currentType.defaultValue)))}
                inputProps={{
                  min: currentType.min,
                  max: currentType.max,
                }}
                helperText={`${currentType.min}-${currentType.max} ${scheduleType}`}
                sx={{ width: 200 }}
                size="small"
              />
              {currentType?.warning && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {currentType.warning}
                </Alert>
              )}
            </div>
          )}

          {/* Time Picker for Daily/Weekly/Monthly */}
          {currentType?.showTimePicker && (
            <div className="row">
              <div className="col-lg-6 col-md-12">
                <div className='group-form'>
                  <label>Hour</label>
                  <TextField
                    placeholder="Hour"
                    type="number"
                    value={hour}
                    onChange={(e) => setHour(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
                    inputProps={{ min: 0, max: 23 }}
                    helperText="0-23"
                    sx={{ width: 100 }}
                    size="small"
                  />
                </div>
              </div>
              <div className="col-lg-6 col-md-12">
                <div className='group-form'>
                  <label>Minute</label>
                  <TextField
                    placeholder="Minute"
                    type="number"
                    value={minute}
                    onChange={(e) => setMinute(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                    inputProps={{ min: 0, max: 59 }}
                    helperText="0-59"
                    sx={{ width: 100 }}
                    size="small"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Day Selector for Weekly */}
          {currentType?.showDaySelector && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <FormControl sx={{ width: 200 }} size="small">
                <InputLabel>Day of Week</InputLabel>
                <Select
                  value={dayOfWeek}
                  label="Day of Week"
                  onChange={(e) => setDayOfWeek(e.target.value)}
                >
                  {daysOfWeek.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      {day.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Date Selector for Monthly */}
          {currentType?.showDateSelector && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

              <TextField
                label="Day of Month"
                type="number"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 31 }}
                helperText="1-31"
                sx={{ width: 150 }}
                size="small"
              />
            </Box>
          )}

          {/* Cron Expression Input - Visual Builder or Advanced Mode */}
          {currentType?.showCronInput && (
            <Box>
              {/* Mode Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedMode}
                    onChange={(e) => {
                      setAdvancedMode(e.target.checked);
                      // Parse existing cron when switching to visual mode
                      if (!e.target.checked && cronExpression) {
                        parseCronToBuilder(cronExpression);
                      }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CodeIcon fontSize="small" />
                    <Typography variant="body2">Advanced Mode (Raw Cron)</Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              {/* Advanced Mode: Simple Text Input */}
              {advancedMode ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Cron Expression"
                    value={cronExpression}
                    onChange={(e) => setCronExpression(e.target.value)}
                    placeholder="0 9 * * *"
                    helperText="Format: minute hour day-of-month month day-of-week (5-field)"
                    size="small"
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Examples: "0 9 * * *" (daily at 9 AM), "*/15 * * * *" (every 15 minutes)
                  </Typography>

                  {/* Show description of current cron */}
                  {cronExpression && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Description: {getCronDescription(cronExpression)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                /* Visual Mode: Full Visual Builder */
                renderVisualCronBuilder()
              )}
            </Box>
          )}
        </Box>

        {/* Preview Section */}
        <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Preview:
          </Typography>
          <Chip
            label={previewText}
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.875rem', height: '28px' }}
          />
        </Box>
      </Box>
    </Box >
  );
};

export default ScheduleTriggerConfig;